import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ChevronRight, Grid, List, Package, Star, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { Category } from '@/services/productsService';
import { Product } from '@/services/types/domain';
import productsService from '@/services/productsService';
import ProductCard from '@/components/product/ProductCard';
import ProductCardSkeleton from '@/components/product/ProductCardSkeleton';
import CategoryCard from './CategoryCard';
import Button from '@/components/ui/Button';
import { toast } from 'react-hot-toast';
import { cn } from '@/utils/utils';

const CategoryDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();

    const [category, setCategory] = useState<Category | null>(null);
    const [subcategories, setSubcategories] = useState<Category[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [stats, setStats] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

    // Filtros
    const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'created_desc');

    const categoryId = parseInt(id || '0');

    useEffect(() => {
        if (categoryId > 0) {
            loadCategoryData();
        }
    }, [categoryId]);

    // Actualizar URL cuando cambien los filtros
    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        if (sortBy !== 'created_desc') {
            params.set('sort', sortBy);
        } else {
            params.delete('sort');
        }
        setSearchParams(params);
    }, [sortBy, setSearchParams, searchParams]);

    const loadCategoryData = async () => {
        try {
            setIsLoading(true);
            const response = await productsService.getCategoryById(categoryId);

            // Extraer datos de la respuesta unificada del backend
            const { category: categoryData, subcategories: subcategoriesData, products: productsData, stats: statsData } = response;

            setCategory(categoryData);
            setSubcategories(subcategoriesData);
            setStats(statsData);

            // Convertir productos del backend al formato esperado
            const adaptedProducts = productsData.map(adaptBackendProduct);
            setProducts(adaptedProducts);

            console.log('Category data loaded:', response);
        } catch (error: any) {
            console.error('Error loading category:', error);
            toast.error(error.response?.data?.message || 'Error al cargar la categoría');
        } finally {
            setIsLoading(false);
        }
    };

    // Función para ordenar productos localmente
    const sortProducts = (products: Product[]) => {
        const sortedProducts = [...products];

        switch (sortBy) {
            case 'name_asc':
                return sortedProducts.sort((a, b) => a.name.localeCompare(b.name));
            case 'price_asc':
                return sortedProducts.sort((a, b) => a.price - b.price);
            case 'price_desc':
                return sortedProducts.sort((a, b) => b.price - a.price);
            case 'created_desc':
            default:
                return sortedProducts; // Ya vienen ordenados por fecha del backend
        }
    };

    // Función para formatear precio
    const formatPrice = (price: string | number): string => {
        const numPrice = typeof price === 'string' ? parseFloat(price) : price;
        return new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(numPrice);
    };

    // Convertir producto del backend al formato esperado por ProductCard
    const adaptBackendProduct = (backendProduct: any): any => {
        const defaultImage = backendProduct.primary_image ? {
            id: 1,
            product_id: backendProduct.id,
            image_url: backendProduct.primary_image,
            alt_text: backendProduct.name,
            sort_order: 0,
            is_primary: true,
            created_at: new Date().toISOString(),
        } : undefined;

        return {
            id: backendProduct.id,
            name: backendProduct.name,
            slug: backendProduct.slug,
            description: '',
            short_description: '',
            sku: `SKU-${backendProduct.id}`,
            price: parseFloat(backendProduct.price),
            compare_price: backendProduct.compare_price ? parseFloat(backendProduct.compare_price) : null,
            cost_price: 0,
            stock_quantity: 10,
            weight: 0,
            dimensions: '',
            category_id: categoryId,
            brand: '',
            status: 'active',
            is_featured: false,
            meta_title: backendProduct.name,
            meta_description: '',
            tags: '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            category: category || {} as Category,
            images: defaultImage ? [defaultImage] : [],
            variants: [],
            attributes: [],
            average_rating: backendProduct.rating_average ? parseFloat(backendProduct.rating_average) : 0,
            reviews_count: 0,
            has_variants: false,
            primary_image: defaultImage,
        };
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50">
                {/* Header skeleton */}
                <div className="bg-white border-b">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="h-6 bg-gray-200 rounded w-1/3 animate-pulse"></div>
                    </div>
                </div>

                <div className="bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="h-8 bg-gray-200 rounded w-1/2 mb-4 animate-pulse"></div>
                                <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="w-24 h-20 bg-gray-200 rounded animate-pulse"></div>
                                <div className="w-24 h-20 bg-gray-200 rounded animate-pulse"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <ProductCardSkeleton key={i} />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!category) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Categoría no encontrada
                    </h3>
                    <p className="text-gray-600 mb-6">
                        La categoría que buscas no existe o no está disponible.
                    </p>
                    <Link to="/categories">
                        <Button variant="outline">Ver otras categorías</Button>
                    </Link>
                </div>
            </div>
        );
    }

    const sortedProducts = sortProducts(products);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Breadcrumb */}
            <div className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <nav className="flex items-center space-x-2 text-sm">
                        <Link to="/" className="text-gray-500 hover:text-gray-700">
                            Inicio
                        </Link>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                        <Link to="/categories" className="text-gray-500 hover:text-gray-700">
                            Categorías
                        </Link>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                        {(category as any)?.parent_name && (
                            <>
                                <span className="text-gray-500">{(category as any).parent_name}</span>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                            </>
                        )}
                        <span className="text-gray-900 font-medium">{category.name}</span>
                    </nav>
                </div>
            </div>

            {/* Header de la categoría */}
            <div className="bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-4">
                                <Link
                                    to="/categories"
                                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                </Link>
                                <h1 className="text-3xl font-bold text-gray-900">
                                    {category.name}
                                </h1>
                            </div>

                            {category.description && (
                                <p className="text-gray-600 max-w-2xl">
                                    {category.description}
                                </p>
                            )}

                            {/* Información de rango de precios */}
                            <div className="mt-4 flex items-center space-x-6 text-sm text-gray-600">
                                {stats && (
                                    <>
                                        <span>
                                            Desde {formatPrice(stats.min_price)} hasta {formatPrice(stats.max_price)}
                                        </span>
                                        {stats.average_rating && parseFloat(stats.average_rating) > 0 && (
                                            <div className="flex items-center space-x-1">
                                                <Star className="w-4 h-4 text-yellow-400 fill-current" />
                                                <span>{parseFloat(stats.average_rating).toFixed(1)} promedio</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Estadísticas */}
                        {/* {stats && (
                            <div className="grid grid-cols-2 gap-4 text-center ml-8">
                                <div className="bg-blue-50 rounded-lg p-4 min-w-[100px]">
                                    <div className="flex items-center justify-center text-blue-600 mb-2">
                                        <Package className="w-5 h-5" />
                                    </div>
                                    <div className="text-2xl font-bold text-blue-600">
                                        {stats.total_products}
                                    </div>
                                    <div className="text-sm text-blue-600">Productos</div>
                                </div>

                                <div className="bg-green-50 rounded-lg p-4 min-w-[100px]">
                                    <div className="flex items-center justify-center text-green-600 mb-2">
                                        <DollarSign className="w-5 h-5" />
                                    </div>
                                    <div className="text-lg font-bold text-green-600">
                                        {formatPrice(stats.average_price)}
                                    </div>
                                    <div className="text-sm text-green-600">Precio promedio</div>
                                </div>
                            </div>
                        )} */}
                    </div>
                </div>
            </div>



            {/* Controles de vista y filtros */}
            <div className="bg-white border-t">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900">
                            Productos {stats && `(${stats.total_products})`}
                        </h2>

                        <div className="flex items-center space-x-4">
                            {/* Ordenamiento */}
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="created_desc">Más Recientes</option>
                                <option value="name_asc">Nombre A-Z</option>
                                <option value="price_asc">Precio: Menor a Mayor</option>
                                <option value="price_desc">Precio: Mayor a Menor</option>
                            </select>

                            {/* Modo de vista */}
                            <div className="flex items-center border border-gray-300 rounded-md">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={cn(
                                        'p-2 transition-colors',
                                        viewMode === 'grid'
                                            ? 'bg-blue-100 text-blue-600'
                                            : 'text-gray-400 hover:text-gray-600'
                                    )}
                                >
                                    <Grid className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={cn(
                                        'p-2 transition-colors',
                                        viewMode === 'list'
                                            ? 'bg-blue-100 text-blue-600'
                                            : 'text-gray-400 hover:text-gray-600'
                                    )}
                                >
                                    <List className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lista de productos */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {sortedProducts.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="text-center py-12"
                    >
                        <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            No hay productos en esta categoría
                        </h3>
                        <p className="text-gray-600 mb-6">
                            Vuelve más tarde o explora otras categorías.
                        </p>
                        <Link to="/categories">
                            <Button variant="outline">Ver otras categorías</Button>
                        </Link>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className={cn(
                            'grid gap-6',
                            viewMode === 'grid'
                                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                        )}
                    >
                        {sortedProducts.map((product, index) => (
                            <motion.div
                                key={product.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                            >
                                <ProductCard
                                    product={product}
                                    showQuickAdd
                                    showWishlist
                                />
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </div>

            {/* Información adicional */}
            {products.length > 0 && stats && (
                <div className="bg-white border-t">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                        <div className="text-center text-sm text-gray-500">
                            Mostrando {products.length} de {stats.total_products} productos de esta categoría
                        </div>
                    </div>
                </div>
            )}
            {/* Subcategorías */}
            {subcategories.length > 0 && (
                <div className="bg-white border-t">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">
                            Subcategorías
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            {/* {subcategories.map((subcategory) => (
                                <Link
                                    key={subcategory.id}
                                    to={`/categories/${subcategory.id}`}
                                    className="group p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all"
                                >
                                    <div className="text-center">
                                        <h3 className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors text-sm">
                                            {subcategory.name}
                                        </h3>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {(subcategory as any).product_count || 0} productos
                                        </p>
                                    </div>
                                </Link>
                            ))} */}
                            {subcategories.map((subcategory, index) => (
                                <CategoryCard
                                    key={subcategory.id}
                                    category={subcategory}
                                    variant="compact"
                                    index={index}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoryDetailPage;